// pfs-badge.ts — generisches Status-Pill / Count-Badge (PFS-596, Epic PFS-591).
//
// Vereint die zwei bisher 3x duplizierten Muster:
//   • STATUS-PILL (soft): heller Tint-Hintergrund + farbiger Text, Varianten
//     neutral/success/warning/error/info — wie .badge in mfa-settings/mfa-enroll.
//   • COUNT-BADGE (solid, rund): satter Hintergrund + heller Text, min-width=height
//     — wie das absolute .badge auf der notification-bell (data-count, data-style=solid).
//
// Brandbar/themebar ohne adoptStyles: alle Farben kommen aus --pfs-color-*-Tokens,
// die als CSS-Custom-Properties durch die Shadow-Grenze vererben (Document-:root →
// Brand-Overlay → Dark). Positionierung (z. B. absolut auf einem Icon) ist Sache des
// Consumers; das WC ist :host{display:inline-flex} und bringt nur das Pill.
//
// Attribute (alle data-*):
//   data-variant   neutral (Default) | success | warning | error | info
//   data-style     soft (Default) | solid
//   data-count     Zahl → Count-Modus (rund); >99 → "99+"; 0 → versteckt
//                  (ausser data-show-zero gesetzt). Reaktiv (MutationObserver).
//   data-show-zero ""/gesetzt → Count 0 wird angezeigt statt versteckt
//   data-label     Label-Text (reaktiv); ohne data-label = Light-DOM-Textinhalt
//   data-interactive ""/gesetzt → klickbarer Status-Pill: rendert ein echtes
//                  <button> im Shadow (Tastatur/Fokus/role) statt <span>. Der
//                  Click bubbelt nativ (composed) → Consumer: addEventListener("click").
//
// Beispiele:
//   <pfs-badge data-variant="success">Aktiv</pfs-badge>
//   <pfs-badge data-variant="warning">Ausstehend</pfs-badge>
//   <pfs-badge data-variant="error" data-style="solid" data-count="3"></pfs-badge>
//   <pfs-badge data-variant="success" data-interactive>Rückmeldung abgegeben</pfs-badge>
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";

const STYLES = `
:host { display: inline-flex; vertical-align: middle; }
:host([hidden]) { display: none; }
.b {
  display: inline-flex; align-items: center; justify-content: center;
  font-family: var(--pfs-font-family, inherit);
  font-weight: 600; line-height: 1; white-space: nowrap;
  border-radius: 9999px;
  font-size: 0.7rem; padding: 0.12rem 0.45rem;
  box-sizing: border-box;
}
/* Count-Modus: rund, min-width = height */
:host([data-count]) .b {
  min-width: 1.125rem; height: 1.125rem; padding: 0 0.3rem;
  font-size: 0.625rem; font-weight: 700;
}
/* soft (Default): heller Tint + farbiger Text */
.b { background: var(--pfs-color-surface-sunken); color: var(--pfs-color-on-surface-muted); }
.b.success { background: var(--pfs-color-success-container); color: var(--pfs-color-on-success-container); }
.b.warning { background: var(--pfs-color-warning-container); color: var(--pfs-color-on-warning-container); }
.b.error   { background: var(--pfs-color-danger-container); color: var(--pfs-color-on-danger-container); }
.b.info    { background: var(--pfs-color-info-container); color: var(--pfs-color-on-info-container); }
/* solid: satter Hintergrund + heller Text (Count-Badges, Akzent-Pills) */
.b.solid { color: var(--pfs-color-on-primary); }
.b.solid.neutral { background: var(--pfs-color-on-surface-muted); }
.b.solid.success { background: var(--pfs-color-success); }
.b.solid.warning { background: var(--pfs-color-warning); }
.b.solid.error   { background: var(--pfs-color-danger); }
.b.solid.info    { background: var(--pfs-color-primary); }
/* interaktiv: das Pill IST ein <button> — UA-Chrome resetten, Klick-Affordanz +
   sichtbarer Fokus. Variante/Farbe/Form erbt es aus .b (gleiche Klasse). */
button.b { -webkit-appearance: none; appearance: none; border: 0; margin: 0; cursor: pointer; }
.b.interactive:hover { filter: brightness(0.95); }
.b.interactive:focus-visible { outline: 2px solid var(--pfs-color-accent); outline-offset: 2px; }
`;

const VARIANTS = ["neutral", "success", "warning", "error", "info"];

const isInteractive = (host: HTMLElement): boolean => host.dataset.interactive != null;

// Interaktiv → echtes <button> (a11y: Tastatur/Fokus/role), sonst passives <span>.
function makeInner(host: HTMLElement): HTMLElement {
  if (isInteractive(host)) {
    const b = document.createElement("button");
    b.type = "button";
    return b;
  }
  return document.createElement("span");
}

function paint(host: HTMLElement, inner: HTMLElement): void {
  const v = host.dataset.variant ?? "neutral";
  const variant = VARIANTS.includes(v) ? v : "neutral";
  const solid = host.dataset.style === "solid";
  inner.className = "b " + variant + (solid ? " solid" : "") + (isInteractive(host) ? " interactive" : "");

  if (host.dataset.count != null) {
    const n = parseInt(host.dataset.count, 10) || 0;
    const showZero = host.dataset.showZero != null;
    host.hidden = n === 0 && !showZero;
    inner.textContent = n > 99 ? "99+" : String(n);
  } else {
    host.hidden = false;
    inner.textContent = host.dataset.label ?? (host.textContent ?? "").trim();
  }
}

defineComponent({
  tagName: "pfs-badge",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;
    const root = ctx.shadowRoot;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);
    let inner = makeInner(host);
    root.appendChild(inner);
    paint(host, inner);
    // Reaktiv auf Attribut-Aenderungen (Count/Variante/Style/Label/Interactive).
    const obs = new MutationObserver(() => {
      // data-interactive umgeschaltet → Element-Typ (button/span) tauschen.
      if (isInteractive(host) !== (inner.tagName === "BUTTON")) {
        const next = makeInner(host);
        inner.replaceWith(next);
        inner = next;
      }
      paint(host, inner);
    });
    ctx.cleanup.push(() => obs.disconnect());
    obs.observe(host, {
      attributes: true,
      attributeFilter: ["data-variant", "data-style", "data-count", "data-show-zero", "data-label", "data-interactive"],
    });
  },
});
